import PropTypes from 'prop-types'

import defaultProps from '@/components/Form/defaultProps'
import Field from '@/components/Form/Field'
import propTypes from '@/components/Form/propTypes'
import cn from '@/utilities/cn'

import InputList from './InputList'

import './index.scss'

/**
 * @example
 * <AutoComplete
 *   description={<FormattedMessage id="SomeDescription" defaultMessage="Some Text" />}
 *   label={<FormattedMessage id="SomeLabel" defaultMessage="Some Label" />}
 *   name="someField"
 *   onResultClick={result => ({ display: result.name, value: result.id })}
 *   onSearchChange={searchTerm => this.props.actions.search(searchTerm)}
 *   placeholder={<FormattedMessage id="SomePlaceholder" defaultMessage="Some Placeholder" />}
 *   renderResult={result => <span>{result.name}</span>}
 *   results={[{ id: 'user1', name: 'User 1' }, { id: 'user2', name: 'User 2' }, { id: 'user3', name: 'User 3' }]}
 *   required
 * />
 * @param {object} options
 * @param {Function} options.onChange - Called when a result is clicked from the list.
 * @param {boolean} options.clearOnInput - Should formik state be cleared when user types over a selected value.
 * @param {boolean} options.isAsyncSearch- Is autocomplete search async
 * @param {Function} options.onResultClick - Called with object for a clicked result. Must return an object with `display` and `value` properties.
 * @param {Function} options.onSearchChange - Called when the value within the search input field changes.
 * @param {Function} options.renderResult - Called with object for a result. Must return a renderable element that visually represents the result in the list.
 * @param {object[]} [options.results] - List of results to render in the list. Each result object must have an `id` property.
 * @param {boolean} options.showResultsOnFocus- Should results list display when input is focused
 * @param {boolean} options.acceptsPropsValue - Accepts props value
 */
const AutoComplete = ({ className, onChange, clearOnInput, ...props }: any) => (
  <Field
    {...props}
    className={cn('form__autocomplete', { 'form__field--icon': !!props.icon }, className)}
    render={({ icon, ...field }: any) => (
      <>
        <InputList {...field} onChange={onChange} clearOnInput={clearOnInput} />
        {!!icon && <span className="field__icon">{icon}</span>}
      </>
    )}
  />
)

AutoComplete.displayName = 'AutoComplete'

AutoComplete.propTypes = {
  ...propTypes,
  icon: PropTypes.element,
  onResultClick: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  renderResult: PropTypes.func.isRequired,
  results: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })),
  type: PropTypes.string,
  isSearching: PropTypes.bool,
  isAsyncSearch: PropTypes.bool,
  valueType: PropTypes.string,
  clearOnInput: PropTypes.bool,
  spellCheck: PropTypes.bool,
  showResultsOnFocus: PropTypes.bool,
  acceptsPropsValue: PropTypes.bool,
  value: PropTypes.string
}

AutoComplete.defaultProps = {
  ...defaultProps,
  icon: null,
  placeholder: '',
  results: null,
  type: 'text',
  isSearching: false,
  isAsyncSearch: true,
  valueType: '',
  clearOnInput: true,
  spellCheck: false
}

export default AutoComplete
